import React, { useEffect, useState } from "react";
import IconEye from "../../../components/icons/IconEye";
import useEscClose from "../../useEscClose";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";
import { shouldRunOnce } from "../../../utils/devOnce";
import { getActorType } from "../../../utils/auth"; // Importamos getActorType

// Componentes de diseño
import ListHeader from "../../../components/listing/ListHeader";
import ListFooter from "../../../components/listing/ListFooter";
import DynamicTable from "../../../components/listing/DynamicTable";

// Componentes hijos (ASUMO QUE ESTOS EXISTEN O SE CREARÁN, SI NO, DARÁ ERROR)
import FiltroAuditoria from "./FiltroAuditoria";
import VerAuditoria from "./VerAuditoria";

const DEFAULT_PAGE_SIZE = 30;

// Helper para formatear fecha
const formatoFechaHora = (fecha) => {
  if (!fecha) return "";

  // 1. Intentamos crear un objeto fecha
  const d = new Date(fecha);

  // 2. Verificamos si es válida.
  // Si da "Invalid Date" (NaN) es porque el backend ya envió un texto como "22-ene-2026"
  if (isNaN(d.getTime())) {
    return fecha; // ¡Devolvemos el texto original tal cual!
  }

  // 3. Si es una fecha válida (ISO), la formateamos nosotros
  try {
    return d.toLocaleString("es-PY", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch (e) {
    return fecha;
  }
};

export default function ListarAuditoria() {
  // ===========================
  //       ESTADOS Y DATA
  // ===========================
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);

  const [paginaActual, setPaginaActual] = useState(1);
  const [porPagina, setPorPagina] = useState(DEFAULT_PAGE_SIZE);
  const [totalBackend, setTotalBackend] = useState(0);

  // Filtros
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [filtros, setFiltros] = useState({});
  useEscClose(() => setMostrarFiltros(false), mostrarFiltros);

  // Ver detalle
  const [idSeleccionado, setIdSeleccionado] = useState(null);
  useEscClose(() => setIdSeleccionado(null), idSeleccionado != null);

  const actorType = getActorType();

  // ===========================
  //       FETCH LISTADO
  // ===========================
  const fetchList = async (
    pageIndex = paginaActual - 1,
    size = porPagina,
    filtrosAplicados = filtros,
  ) => {
    setCargando(true);
    try {
      // Usamos la nueva ruta definida en apiRoutes
      const url = apiRoutes.auditoria.listUrl({
        page: pageIndex,
        size: size,
        ...filtrosAplicados,
      });

      const data = await appFetchJson(url);

      // Soporte para Page<T> de Spring Data
      const contenido = data?.items || data?.content || [];
      const total = data?.totalElements ?? data?.total ?? contenido.length;

      setItems(contenido);
      setTotalBackend(total);
    } catch (err) {
      console.error("Error al obtener auditoría:", err);
      setItems([]);
      setTotalBackend(0);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (!shouldRunOnce("ListarAuditoria:init")) return;
    fetchList(0, porPagina, filtros);
    // eslint-disable-next-line
  }, []);

  // ===========================
  //       ACCIONES
  // ===========================
  const cambiarPagina = (num) => {
    setPaginaActual(num);
    fetchList(num - 1, porPagina, filtros);
  };

  const manejarCambioCantidad = (size) => {
    setPorPagina(size);
    setPaginaActual(1);
    fetchList(0, size, filtros);
  };

  // ====================================================================
  // DEFINICIÓN DE COLUMNAS
  // Basado en captura Legacy: Fecha | Usuario | Tarea | Tabla | Cod Audit | Operacion | Acción
  // ====================================================================
  const allColumns = [
    // 1. FECHA - Alineada a la izquierda (Debajo de la "F")
    {
      header: "Fecha",
      accessor: "dataFecha",
      type: "text", // Cambiado de "center" a "text" si tu DynamicTable lo soporta, o forzado con clase
      width: "12%",
      // CAMBIO: 'text-left' en vez de 'text-center'. 'pl-0' o 'pl-2' para ajustar el margen izquierdo exacto.
      cellClass: "font-medium text-gray-700 text-xs text-left pl-1",
      render: (row) => formatoFechaHora(row.dataFecha),
    },

    // 2. USUARIO (10%)
    {
      header: "Usuario",
      accessor: "descricaoUsuario",
      type: "text",
      width: "10%",
      cellClass: "text-gray-600 text-xs break-all font-semibold",
    },

    // 3. TAREA - Modificada para limitar líneas
    {
      header: "Tarea",
      accessor: "descricaoTarea",
      type: "text",
      width: "40%",
      cellClass:
        "text-gray-600 text-xs break-all whitespace-normal leading-tight",
      // === AGREGAR ESTA FUNCIÓN RENDER ===
      render: (row) => (
        <div
          title={row.descricaoTarea} // Muestra el texto completo al dejar el mouse encima
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 4, // Número máximo de líneas visibles (ajusta a 3 o 4)
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis", // Agrega "..." al final
          }}
        >
          {row.descricaoTarea}
        </div>
      ),
    },

    // 4. TABLA (10%)
    {
      header: "Tabla",
      accessor: "codigoTabla",
      type: "text",
      width: "10%",
      cellClass: "text-xs text-gray-500 font-mono",
    },

    // 5. CODIGO DE AUDITORIA
    {
      header: "Cód. Audit",
      accessor: "id",
      type: "text",
      width: "8%",
      // CAMBIO: Bajamos de 'pl-5' a 'pl-2' para alinearlo con la 'C'
      cellClass: "text-xs text-gray-800 font-mono text-left pl-2",
    },

    // 6. OPERACION (10%)
    {
      header: "Operación",
      accessor: "codigoTipo",
      type: "center",
      width: "10%",
      cellClass: "text-xs font-bold text-gray-600",
    },

    // 7. ACCIÓN
    {
      header: "Acción",
      accessor: "id",
      type: "action",
      width: "8%",
      render: (row) => (
        // CAMBIO: Usamos 'justify-center' en lugar de 'justify-start'
        <div className="flex items-center justify-center h-full">
          <button
            className="p-1"
            title="Ver Detalle"
            onClick={() => setIdSeleccionado(row.id)}
          >
            <IconEye />
          </button>
        </div>
      ),
    },
  ];

  // Filtrar columnas si es CLIENTE
  const columns =
    actorType === "CLIENTE"
      ? allColumns.filter((col) =>
          ["Fecha", "Usuario", "Tarea", "Operación", "Acción"].includes(
            col.header,
          ),
        )
      : allColumns;

  // ===========================
  //       RENDER
  // ===========================
  return (
    <div className="page">
      <ListHeader
        title={
          actorType === "CLIENTE"
            ? "Control de Cambios"
            : "Auditoría del Sistema"
        }
        // Filtros
        filters={filtros}
        onFilterClick={() => setMostrarFiltros(!mostrarFiltros)}
        onClearFilters={() => {
          setFiltros({});
          setPaginaActual(1);
          fetchList(0, porPagina, {});
        }}
        // Paginación
        pageSize={porPagina}
        onPageSizeChange={manejarCambioCantidad}
        // Auditoría es solo lectura, no hay botón 'Agregar'
        showAdd={false}
      />

      {/* FILTROS DRAWER */}
      {mostrarFiltros && (
        <FiltroAuditoria
          initialFilters={filtros}
          onSubmit={(dataF) => {
            setFiltros(dataF);
            setPaginaActual(1);
            setMostrarFiltros(false);
            fetchList(0, porPagina, dataF);
          }}
          onClose={() => setMostrarFiltros(false)}
        />
      )}

      {/* CARGANDO */}
      {cargando && (
        <div className="mt-2 text-sm text-center text-gray-600">
          Cargando registros...
        </div>
      )}

      {/* TABLA DINÁMICA */}
      {!cargando && <DynamicTable size="L" data={items} columns={columns} />}

      {/* FOOTER PAGINACIÓN */}
      <ListFooter
        page={paginaActual}
        pageSize={porPagina}
        total={totalBackend}
        onPageChange={cambiarPagina}
      />

      {/* MODAL DETALLE */}
      {/* Si detalleData tiene un valor (ID), mostramos el componente */}
      {idSeleccionado && (
        <VerAuditoria
          id={idSeleccionado}
          onClose={() => setIdSeleccionado(null)}
        />
      )}
    </div>
  );
}
